import React, { useEffect, useRef, useState } from "react";
import { Loader2, ShieldX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { claimsFilterAtom } from "../../state/filter";
import { useAuth } from "../AuthContext";
import PageLayout from "../PageLayout";
import { SummaryCard } from "../SummaryCard";
import { Claim, ClaimStatus, VENDOR_OPTIONS, formatStatus } from "./types";
import ClaimsTable, { ImagePreview, matchesClaim } from "./ClaimsTable";
import { ActionsDropdown, CreateMemberForm, CreateUserForm, Modal } from "./AdminForms";
import { API_BASE_URL } from "../../config/api";

// ─── Access Denied ────────────────────────────────────────────────────────────

const AccessDenied: React.FC = () => {
    const navigate = useNavigate();
    return (
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4">
            <div className="text-center space-y-4 max-w-sm">
                <ShieldX className="w-10 h-10 text-red-500 mx-auto" />
                <div>
                    <h1 className="text-lg font-semibold text-white mb-1">Access Denied</h1>
                    <p className="text-sm text-gray-500">This page is restricted to administrators only.</p>
                </div>
                <button
                    onClick={() => navigate("/")}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded transition-colors"
                >
                    Go to Reporting Dashboard
                </button>
            </div>
        </div>
    );
};

// ─── Filter styles ───────────────────────────────────────────────────────────

const INPUT =
    "w-full px-3 py-2 bg-[#1a1a1a] border border-[#2d2d2d] rounded text-sm text-gray-200 focus:outline-none focus:border-blue-600 transition-colors [color-scheme:dark]";
const LABEL = "block text-xs text-gray-500 font-medium uppercase tracking-wide mb-1";

const ALL_STATUSES: ClaimStatus[] = [
    "REQUESTED",
    "ACCEPTED_PICKUP",
    "PRE_QC",
    "IN_SERVICE",
    "POST_QC",
    "DISPATCHED",
    "DELIVERED",
    "REJECTED",
    "CANCELLED",
];

const ALL_VENDORS = VENDOR_OPTIONS || [
    { value: "PERFECTO_CLEANERS_DELHI", label: "Perfecto Cleaners - Delhi" },
    { value: "CLEANSTEPS_LAUNDRY_MUMBAI", label: "Cleansteps Laundry - Mumbai" },
    { value: "WILD_SNEAKER_LAUNDRY_DELHI", label: "Wild Sneaker Laundry - Delhi" },
    { value: "FUTSTEPS_NASHIK", label: "FutSteps - Nashik" },
];

// ─── Service Dashboard ────────────────────────────────────────────────────────

const ServiceDashboard: React.FC = () => {
    const { user, isLoading: authLoading } = useAuth();

    const [claims, setClaims] = useState<Claim[]>([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const [showUserModal, setShowUserModal] = useState(false);
    const [showMemberModal, setShowMemberModal] = useState(false);

    const [previewImages, setPreviewImages] = useState<string[]>([]);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [showPreview, setShowPreview] = useState(false);

    // Filters (shared with table + summary cards)
    const [filters, setFilters] = useRecoilState(claimsFilterAtom);
    const { searchText, statusFilter, vendorFilter, fromDate, toDate } = filters;

    const isAdmin = user?.userName === "omkark@securedstitch.com";

    const updateFilter = <K extends keyof typeof filters>(key: K, value: (typeof filters)[K]) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // ── Fetch claims ──────────────────────────────────────────────────────────

    const fetchClaims = async (refresh = false) => {
        if (refresh) setIsRefreshing(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/claim`);
            const data = await res.json();
            setClaims(data);
        } catch {
            // silent
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (!isAdmin) return;
        fetchClaims();
        intervalRef.current = setInterval(() => fetchClaims(), 900_000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isAdmin]);

    // ── Image preview helpers ─────────────────────────────────────────────────

    const openPreview = (images: string[], i = 0) => {
        setPreviewImages(images);
        setPreviewIndex(i);
        setShowPreview(true);
    };

    const closePreview = () => {
        setShowPreview(false);
        setPreviewImages([]);
    };

    const nextImg = () => setPreviewIndex((p) => (p + 1) % previewImages.length);
    const prevImg = () =>
        setPreviewIndex((p) => (p - 1 + previewImages.length) % previewImages.length);

    // ── Filter data for stats ─────────────────────────────────────────────────

    const filteredClaims = claims.filter((c: Claim) => {
        const matchesSearch = matchesClaim(c, searchText);
        const matchesStatus = statusFilter === "ALL" || c.claimStatus === statusFilter;
        const matchesVendor = vendorFilter === "ALL" || c.vendor === vendorFilter;

        let matchesDate = true;
        if (fromDate || toDate) {
            const claimDate = new Date(c.createdAt);
            claimDate.setHours(0, 0, 0, 0);

            if (fromDate) {
                const fd = new Date(fromDate);
                fd.setHours(0, 0, 0, 0);
                if (claimDate < fd) matchesDate = false;
            }
            if (toDate) {
                const td = new Date(toDate);
                td.setHours(0, 0, 0, 0);
                if (claimDate > td) matchesDate = false;
            }
        }

        return matchesSearch && matchesStatus && matchesVendor && matchesDate;
    });

    // ── Derived stats ─────────────────────────────────────────────────────────

    const todayCount = filteredClaims.filter(
        (c) => new Date(c.createdAt).toDateString() === new Date().toDateString()
    ).length;

    const pendingCount = filteredClaims.filter((c) => c.claimStatus === "REQUESTED").length;

    const inProgressCount = filteredClaims.filter(
        (c) =>
            c.claimStatus === "ACCEPTED_PICKUP" ||
            c.claimStatus === "PRE_QC" ||
            c.claimStatus === "IN_SERVICE" ||
            c.claimStatus === "POST_QC" ||
            c.claimStatus === "DISPATCHED"
    ).length;

    const deliveredCount = filteredClaims.filter((c) => c.claimStatus === "DELIVERED").length;
    const rejectedCount = filteredClaims.filter((c) => c.claimStatus === "REJECTED").length;
    const cancelledCount = filteredClaims.filter((c) => c.claimStatus === "CANCELLED").length;
    const withReviewsCount = filteredClaims.filter((c) => c.rating).length;

    // ── Auth guard ────────────────────────────────────────────────────────────

    if (authLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center gap-2 text-gray-400 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading…
            </div>
        );
    }

    if (!isAdmin) return <AccessDenied />;

    const summaries = [
        { label: "Total Claims", value: filteredClaims.length.toString() },
        { label: "Today", value: todayCount.toString() },
        { label: "Pending", value: pendingCount.toString() },
        { label: "In Progress", value: inProgressCount.toString() },
        { label: "Delivered", value: deliveredCount.toString() },
        { label: "Rejected", value: rejectedCount.toString() },
        { label: "Cancelled", value: cancelledCount.toString() },
        { label: "With Reviews", value: withReviewsCount.toString() },
    ];

    return (
        <>
            <PageLayout
                title="Service Dashboard"
                rightContent={
                    <ActionsDropdown
                        onCreateMember={() => setShowMemberModal(true)}
                        onCreateUser={() => setShowUserModal(true)}
                    />
                }
            >
                {/* ── 1. FILTERS (ABOVE) ────────────────────────────────────── */}
                <div className="bg-[#111111] border border-[#2d2d2d] rounded p-4 mb-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* From */}
                        <div>
                            <label className={LABEL}>From</label>
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => updateFilter("fromDate", e.target.value)}
                                className={`${INPUT} cursor-pointer`}
                            />
                        </div>

                        {/* To */}
                        <div>
                            <label className={LABEL}>To</label>
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => updateFilter("toDate", e.target.value)}
                                className={`${INPUT} cursor-pointer`}
                            />
                        </div>

                        {/* Status */}
                        <div>
                            <label className={LABEL}>Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => updateFilter("statusFilter", e.target.value)}
                                className={`${INPUT} cursor-pointer`}
                            >
                                <option value="ALL">All Statuses</option>
                                {ALL_STATUSES.map((s) => (
                                    <option key={s} value={s}>
                                        {formatStatus(s)}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Vendor */}
                        <div>
                            <label className={LABEL}>Vendor</label>
                            <select
                                value={vendorFilter}
                                onChange={(e) => updateFilter("vendorFilter", e.target.value)}
                                className={`${INPUT} cursor-pointer`}
                            >
                                <option value="ALL">All Vendors</option>
                                {ALL_VENDORS.map((v) => (
                                    <option key={v.value} value={v.value}>
                                        {v.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* ── 2. SUMMARY CARDS (BELOW FILTERS) ───────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 w-full mb-5">
                    {summaries.map((s) => (
                        <SummaryCard key={s.label} title={s.label} value={s.value} />
                    ))}
                </div>

                {/* ── 3. CLAIMS TABLE ────────────────────────────────────────── */}
                <ClaimsTable
                    claims={claims}
                    loading={loading}
                    isRefreshing={isRefreshing}
                    onRefresh={() => fetchClaims(true)}
                    onImageClick={openPreview}
                />
            </PageLayout>

            {/* ── Admin Modals ─────────────────────────────────────────────── */}
            <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)} title="Create User" wide>
                <CreateUserForm onClose={() => setShowUserModal(false)} />
            </Modal>

            <Modal isOpen={showMemberModal} onClose={() => setShowMemberModal(false)} title="Create Member" wide>
                <CreateMemberForm onClose={() => setShowMemberModal(false)} />
            </Modal>

            {/* ── Image Preview Lightbox ───────────────────────────────────── */}
            {showPreview && (
                <ImagePreview
                    images={previewImages}
                    index={previewIndex}
                    onClose={closePreview}
                    onNext={nextImg}
                    onPrev={prevImg}
                />
            )}

            <style>{`
                input[type="date"]::-webkit-calendar-picker-indicator {
                    filter: invert(0.5);
                    cursor: pointer;
                }
            `}</style>
        </>
    );
};

export default ServiceDashboard;